/**
 * jaText — natural Japanese line-breaking + terminology protection.
 *
 * Why: Japanese has no spaces, so browsers break lines anywhere — including the
 * middle of compound nouns (ETCカード → ETCカ / ード), which reads as unprofessional
 * and erodes trust. This module wraps Japanese copy so lines break ONLY at natural
 * 文節 (phrase) boundaries, computed by Google's BudouX, and NEVER inside a
 * protected glossary term.
 *
 * Usage:  <Jp>毎日のETC利用明細を自動でお届けします。</Jp>
 *         <Jp as="h1" className="text-4xl">...</Jp>
 * Only pass plain strings as children (not nested JSX).
 */
import { Fragment, useMemo } from 'react';
import { loadDefaultJapaneseParser } from 'budoux';

const parser = loadDefaultJapaneseParser();

/**
 * Locked glossary — preferred terms that must NEVER be split across a line break.
 * Keep longest-first so multi-word terms win over their substrings.
 * Mirrors /app/memory/JA_GLOSSARY.md (single source of truth for terminology).
 */
export const PROTECTED_TERMS = [
  'ETC利用照会サービス',
  'ETC利用明細', 'ETC利用履歴', 'ETC利用証明書', 'ETC利用レポート', 'ETC利用照会',
  'ETCマイレージサービス', 'PDF・CSVレポート', 'PDF・CSV',
  'ETCカード', 'ETC2.0', 'ETCコーポレートカード',
  '個人事業主', '法人・個人事業主', '確定申告', '経費精算', 'インボイス制度',
  '車両管理', '利用履歴', '先行受付', 'クレジットカード',
  'Japan Toll Receipts',
];

/** Break indices (within `text`) that fall inside a protected term are removed. */
function protectedBreakBlocklist(text) {
  const blocked = new Set();
  for (const term of PROTECTED_TERMS) {
    let idx = text.indexOf(term);
    while (idx >= 0) {
      for (let b = idx + 1; b < idx + term.length; b++) blocked.add(b);
      idx = text.indexOf(term, idx + 1);
    }
  }
  return blocked;
}

/** Returns phrase chunks: BudouX boundaries minus any inside a protected term. */
export function segmentJa(text) {
  if (!text) return [];
  const segs = parser.parse(text);
  const blocked = protectedBreakBlocklist(text);
  const chunks = [];
  let buf = '';
  let pos = 0;
  for (let i = 0; i < segs.length; i++) {
    buf += segs[i];
    pos += segs[i].length;
    const isLast = i === segs.length - 1;
    // A break is allowed here only if BudouX put one AND it's not inside a term.
    if (isLast || !blocked.has(pos)) {
      chunks.push(buf);
      buf = '';
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

export function Jp({ children, as: Tag = 'span', className, ...rest }) {
  const chunks = useMemo(
    () => (typeof children === 'string' ? segmentJa(children) : null),
    [children],
  );
  if (chunks === null) {
    // Non-string children: render as-is (caller is responsible for breaks).
    return <Tag className={className} {...rest}>{children}</Tag>;
  }
  return (
    <Tag className={className} style={{ wordBreak: 'keep-all', ...(rest.style || {}) }} {...rest}>
      {chunks.map((c, i) => (
        <Fragment key={i}>
          {i > 0 && <wbr />}
          {/* keep-all keeps the 文節 phrase together at normal widths; the
              <wbr/> between chunks supplies the natural break points; and
              overflow-wrap:anywhere is an emergency break so a single long
              phrase can never overflow a narrow (mobile) viewport. */}
          <span style={{ wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{c}</span>
        </Fragment>
      ))}
    </Tag>
  );
}

export default Jp;
